import React, { useEffect, useMemo, useRef, useState } from "react";
import "./CustomSelect.css";

/**
 * Lightweight custom dropdown. Themed via _theme.css tokens.
 * options: [{ value, label, icon?, hint? }]
 *   { heading: true, label }           → non-selectable group label
 *   { action: true, label, onSelect }  → runs onSelect and keeps the menu open (e.g. "show more")
 * searchable: show a type-to-filter box at the top of the open menu (for long lists).
 */
export default function CustomSelect({
  value,
  options = [],
  onChange,
  placeholder = "Select…",
  disabled = false,
  ariaLabel,
  size = "md",
  flat = false,
  searchable = false,
}) {
  const [open, setOpen] = useState(false);
  const [focusIdx, setFocusIdx] = useState(-1);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  const active = options.find((o) => o.value === value);
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!searchable || !q) return options;
    return options.filter((o) => !o.heading && !o.action && (String(o.label).toLowerCase().includes(q) || String(o.hint || "").toLowerCase().includes(q)));
  }, [options, query, searchable]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") { setOpen(false); return; }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusIdx((i) => Math.min(visible.length - 1, i + 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusIdx((i) => Math.max(0, i - 1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const pick = visible[focusIdx] || (searchable && visible.find((o) => !o.heading));
        if (pick?.action) pick.onSelect?.();
        else if (pick && !pick.heading) {
          onChange?.(pick.value);
          setOpen(false);
        }
      }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, focusIdx, visible, onChange, searchable]);

  useEffect(() => {
    if (open) {
      setQuery("");
      const i = Math.max(0, options.findIndex((o) => o.value === value));
      setFocusIdx(i);
      if (searchable) setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [open, options, value, searchable]);

  useEffect(() => { if (searchable) setFocusIdx(query ? 0 : -1); }, [query, searchable]);

  return (
    <div
      ref={rootRef}
      className={`ca-select-root size-${size} ${open ? "open" : ""} ${disabled ? "disabled" : ""} ${flat ? "flat" : ""}`}
    >
      <button
        type="button"
        className="ca-select-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="ca-select-value">
          {active ? (
            <>
              {active.icon && <span className="ca-select-icon">{active.icon}</span>}
              <span className="ca-select-label">{active.label}</span>
            </>
          ) : (
            <span className="ca-select-placeholder">{placeholder}</span>
          )}
        </span>
        <svg className="ca-select-caret" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
          <path d="M2.5 4.5 6 8l3.5-3.5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="ca-select-menu">
          {searchable && (
            <input
              ref={searchRef}
              className="ca-select-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type to filter…"
              aria-label="Filter options"
            />
          )}
          <ul className="ca-select-list" role="listbox">
            {visible.length === 0 && (
              <li className="ca-select-empty">{query ? "No matches" : "No options"}</li>
            )}
            {visible.map((opt, i) => opt.heading ? (
              <li key={`h-${opt.label}`} className="ca-select-heading" role="presentation">{opt.label}</li>
            ) : opt.action ? (
              <li
                key={`a-${opt.label}`}
                role="option"
                aria-selected={false}
                className={`ca-select-option ca-select-action ${i === focusIdx ? "focused" : ""}`}
                onMouseEnter={() => setFocusIdx(i)}
                onClick={(e) => { e.stopPropagation(); opt.onSelect?.(); }}
              >
                <span className="ca-select-option-text"><span className="ca-select-option-label">{opt.label}</span></span>
              </li>
            ) : (
              <li
                key={opt.value}
                role="option"
                aria-selected={opt.value === value}
                className={`ca-select-option ${opt.value === value ? "selected" : ""} ${i === focusIdx ? "focused" : ""}`}
                onMouseEnter={() => setFocusIdx(i)}
                onClick={() => { onChange?.(opt.value); setOpen(false); }}
              >
                {opt.icon && <span className="ca-select-icon">{opt.icon}</span>}
                <span className="ca-select-option-text">
                  <span className="ca-select-option-label">{opt.label}</span>
                  {opt.hint && <span className="ca-select-option-hint">{opt.hint}</span>}
                </span>
                {opt.value === value && (
                  <svg className="ca-select-check" width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                    <path d="M3 7.5 6 10.5 11 4.5" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
